import { SEVERITY_UI, dashboardHero, poBarPercent, poUrgency } from '@baseline/shared';
import { Link, useNavigate } from 'react-router';

import { useDashboard } from '../api/queries.js';
import { stateForStatusKey } from '../app/stateFilters.js';
import { BarRow } from '../components/BarRow.js';
import { Card } from '../components/Card.js';
import { useCollapsed } from '../components/collapse.js';
import { Donut } from '../components/Donut.js';
import { EmptyState } from '../components/EmptyState.js';
import { CardChevron, SeverityIcon, severityColour } from '../components/icons.js';
import { PageError } from '../components/PageStatus.js';

import type { Dashboard, Severity } from '@baseline/shared';

// Prototype `vDash` (plan Phase 11) on `GET /dashboard`.

const PO_CHIP = { urgent: '', soon: ' mid', far: ' far' } as const;
const PO_BAR = { urgent: 'var(--red)', soon: 'var(--amber)', far: 'var(--faint)' } as const;

function FixFirst({ rows }: { rows: Dashboard['fixFirst'] }) {
  const [open, toggle] = useCollapsed('dash:ff');
  const navigate = useNavigate();
  return (
    <section className="card ffc">
      <button type="button" className="card-h ffh" aria-expanded={open} onClick={toggle}>
        <h3 aria-level={2}>Fix first</h3>
        <span className="hint">Soonest purchase order first</span>
        <CardChevron open={open} />
      </button>
      {open ? (
        <div className="ffl">
          {rows.map((r) => (
            <button
              key={r.alertId}
              type="button"
              className="ff-r"
              onClick={() => void navigate(`/alerts/${r.alertId}`)}
            >
              <SeverityIcon level={r.severity} />
              <span className="ff-t">
                <b>{r.description}</b>
                <span>
                  {r.assemblyName} · <span className="mono-s">{r.partPn}</span>
                </span>
              </span>
              <span className="ff-d">
                <i className="mkA" />
                Design <b>{r.pdmQty ?? '–'}</b> · <i className="mkB" />
                ERP <b>{r.erpQty ?? '–'}</b>
              </span>
              {r.poCode !== null && r.poDays !== null ? (
                <span className={`ff-n po${PO_CHIP[poUrgency(r.poDays)]}`}>
                  {r.poCode} in {r.poDays}d
                </span>
              ) : (
                <span className="ff-n po far">No PO soon</span>
              )}
              <span className="ff-go" aria-hidden="true">
                ›
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}

export default function DashboardPage() {
  const { data: d, error } = useDashboard();
  const navigate = useNavigate();
  if (error) return <PageError error={error} />;
  if (!d) return <div className="pg" aria-busy="true" />;

  const hero = dashboardHero({
    openCount: d.kpis.open,
    waitingCount: d.kpis.waiting,
    fixFirst: d.fixFirst,
  });
  const go = (query: string) => void navigate(`/alerts${query}`);
  const stageMax = Math.max(1, ...d.stages.map((s) => s.count));
  const asmMax = Math.max(1, ...d.byAssembly.map((a) => a.open));

  const legend = (l: Severity) => (
    <button
      key={l}
      type="button"
      className="lg"
      disabled={!d.severityCounts[l]}
      onClick={() => go(`?severity=${String(l)}`)}
    >
      <span className="lg-dot" style={{ background: severityColour(l) }} />
      <span className="lg-l">{SEVERITY_UI[l].label}</span>
      <span className="lg-n">{d.severityCounts[l]}</span>
    </button>
  );

  const kpi = (label: string, n: number, sub: string, query: string) => (
    <button className="kpi" type="button" onClick={() => go(query)}>
      <div className="k-l">{label}</div>
      <div className="k-n">{n}</div>
      <div className="k-s">{sub}</div>
    </button>
  );

  return (
    <div className="pg">
      <div className="ph hero">
        <div>
          <h1>{hero.heading}</h1>
          <p className="ps">{hero.sub}</p>
        </div>
        <div className="ph-r">
          <Link className="btn btn-p" to="/alerts">
            Review alerts
          </Link>
        </div>
      </div>
      <div className="kpis">
        {kpi('Open alerts', d.kpis.open, 'PDM and ERP disagree', '')}
        {kpi('Need your decision', d.kpis.needDecision, 'New or draft ready', '?view=action')}
        {kpi('Waiting on others', d.kpis.waiting, 'Reply or verification', '?view=waiting')}
        {kpi('Closed', d.kpis.closed, 'Verified or accepted', '?view=closed')}
      </div>
      {!d.kpis.open ? (
        <EmptyState title="Everything matches." sub="PDM and ERP agree on every line." />
      ) : (
        <>
          {d.fixFirst.length ? <FixFirst rows={d.fixFirst} /> : null}
          <div className="dgrid2">
            <div className="dcol">
              <Card stateKey="dash:sev" title="Open alerts by severity">
                <div className="donut-wrap">
                  <Donut counts={d.severityCounts} size={132} />
                  <div className="legend">
                    {legend(2)}
                    {legend(1)}
                    {legend(0)}
                  </div>
                </div>
              </Card>
              <Card stateKey="dash:asm" title="Open alerts by assembly">
                <div className="bars">
                  {d.byAssembly.map((a) => (
                    <BarRow
                      key={a.assemblyId}
                      label={a.name}
                      disabled={!a.open}
                      onClick={() => go(`?assemblyId=${a.assemblyId}`)}
                      value={a.open}
                      segments={([2, 1, 0] as const)
                        .filter((l) => a.bySeverity[l])
                        .map((l) => ({
                          width: (a.bySeverity[l] / asmMax) * 100,
                          colour: severityColour(l),
                          title: `${SEVERITY_UI[l].label}: ${String(a.bySeverity[l])}`,
                        }))}
                    />
                  ))}
                </div>
                <div className="ch-k">
                  <span>
                    <i style={{ background: 'var(--red)' }} />
                    High
                  </span>
                  <span>
                    <i style={{ background: 'var(--amber)' }} />
                    Medium
                  </span>
                  <span>
                    <i style={{ background: 'var(--faint)' }} />
                    Low
                  </span>
                </div>
              </Card>
            </div>
            <div className="dcol">
              <Card stateKey="dash:stages" title="Where alerts are right now">
                <div className="bars">
                  {d.stages.map((s) => (
                    <BarRow
                      key={s.statusKey}
                      label={s.label}
                      disabled={!s.count}
                      onClick={() => go(`?state=${stateForStatusKey(s.statusKey) ?? ''}`)}
                      value={s.count}
                      segments={[
                        {
                          width: (s.count / stageMax) * 100,
                          className: s.statusKey === 'new' ? 'f-amber' : 'f-teal',
                        },
                      ]}
                    />
                  ))}
                </div>
              </Card>
              <Card stateKey="dash:po" title="Purchase orders releasing soon" hint="next 14 days">
                <div className="bars">
                  {d.posSoon.length ? (
                    d.posSoon.map((p) => (
                      <BarRow
                        key={`${p.alertId}:${p.poCode}`}
                        label={`${p.poCode} · ${p.partPn}`}
                        onClick={() => void navigate(`/alerts/${p.alertId}`)}
                        value={`in ${String(p.days)} days`}
                        wideValue
                        segments={[
                          { width: poBarPercent(p.days), colour: PO_BAR[poUrgency(p.days)] },
                        ]}
                      />
                    ))
                  ) : (
                    <div className="none">No purchase orders release in the next 14 days.</div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
